// Achievement definitions for Groundwork.
//
// These are the fixed set of milestones a Director can earn. The per-user
// record of which are unlocked lives in Supabase (achievement_unlocks); this
// file is the source of truth for what each one is and how it looks.
//
// To add a new achievement, append an entry with a unique `key`, a `cat` that
// matches a CATEGORIES key, a short `title` and a one-line `desc`. Call
// unlockAchievement(key) from the game when the milestone is hit.

export const CATEGORIES = {
  start: { label: 'Getting started', accent: '#C8F135' }, // lime
  pitch: { label: 'On the pitch', accent: '#4DD8E8' },    // cyan
  board: { label: 'In the boardroom', accent: '#F5C65D' },// gold
  prestige: { label: 'Prestige', accent: '#A78BFA' },     // violet
};

export const ACHIEVEMENTS = [
  // Getting started
  { key: 'first_save', cat: 'start', title: 'New Appointment', desc: 'Start your first save.' },
  { key: 'first_signing', cat: 'start', title: 'Opening Bid', desc: 'Complete your first signing.' },
  { key: 'first_sale', cat: 'start', title: 'Cashing In', desc: 'Sell your first player.' },

  // On the pitch
  { key: 'first_win', cat: 'pitch', title: 'Three Points', desc: 'Win your first league match.' },
  { key: 'academy_debut', cat: 'pitch', title: 'One of Our Own', desc: 'Give an academy player their senior debut.' },
  { key: 'great_escape', cat: 'pitch', title: 'The Great Escape', desc: 'Avoid relegation on the final day of a season.' },

  // In the boardroom
  { key: 'window_profit', cat: 'board', title: 'In the Black', desc: 'Finish a transfer window in profit.' },
  { key: 'wonderkid', cat: 'board', title: 'Gem Unearthed', desc: 'Sign a player under 18 with elite potential.' },
  { key: 'record_sale', cat: 'board', title: 'Record Sale', desc: 'Sell a player for a club-record fee.' },

  // Prestige
  { key: 'promotion', cat: 'prestige', title: 'Going Up', desc: 'Win promotion to a higher division.' },
  { key: 'first_trophy', cat: 'prestige', title: 'Silverware', desc: 'Win your first trophy.' },
  { key: 'loyalty', cat: 'prestige', title: 'The Long Game', desc: 'Reach five seasons at a single club.' },
];

export const TOTAL_ACHIEVEMENTS = ACHIEVEMENTS.length;

// Category icons. One per category so the grid has variety without needing a
// bespoke mark for every achievement. Rendered at currentColor.
export function CategoryIcon({ cat, size = 24 }) {
  const common = {
    width: size, height: size, viewBox: '0 0 24 24', fill: 'none',
    'aria-hidden': true,
  };
  if (cat === 'start') {
    return (
      <svg {...common}>
        <path d="M6 21V4M6 4.5h10.5l-2 3.5 2 3.5H6" stroke="currentColor"
          strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (cat === 'pitch') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="8.4" stroke="currentColor" strokeWidth="1.7" />
        <path d="M12 7.6l3.2 2.3-1.2 3.7h-4L8.8 9.9 12 7.6Z" stroke="currentColor"
          strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
    );
  }
  if (cat === 'board') {
    return (
      <svg {...common}>
        <ellipse cx="12" cy="6.4" rx="6.6" ry="2.6" stroke="currentColor" strokeWidth="1.7" />
        <path d="M5.4 6.4v11.2c0 1.4 3 2.6 6.6 2.6s6.6-1.2 6.6-2.6V6.4M5.4 12c0 1.4 3 2.6 6.6 2.6s6.6-1.2 6.6-2.6"
          stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  // prestige - trophy
  return (
    <svg {...common}>
      <path d="M7 4h10v4a5 5 0 0 1-10 0V4Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M7 5.5H4.5V7a3 3 0 0 0 3 3M17 5.5h2.5V7a3 3 0 0 1-3 3M12 13v3.5M8.5 20h7M9.5 20c0-1.6 1-2.2 2.5-2.2s2.5.6 2.5 2.2"
        stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
